/**
 * Auth gate for admin surfaces — PR-5 D2 aware.
 *
 * Renders children when the current user holds the required builder
 * access `flag` (from `/api/auth/me` — admins come back with every
 * flag true, so `role === "admin"` needs no special-casing). Without
 * a flag prop it falls back to requiring the admin role, preserving
 * the pre-D2 behaviour for surfaces without a dedicated flag.
 * Redirects to `/` otherwise (direct URLs cannot reach the pages —
 * the backend enforces the same flag on every route regardless).
 *
 * Always wrap inside `<ProtectedRoute>` first so unauthenticated users
 * land on `/login`, not `/`.
 */

import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";

import { useAuth } from "@/hooks/useAuth";
import type { UserBuilderAccess } from "@/types";

interface AdminRouteProps {
  children: ReactNode;
  /** Builder access flag that grants this surface (D2). */
  flag?: keyof UserBuilderAccess;
}

export function AdminRoute({ children, flag }: AdminRouteProps): ReactNode {
  const { user } = useAuth();
  const allowed = flag
    ? Boolean(user?.builder_access?.[flag])
    : user?.role === "admin";
  if (!allowed) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
